import { useEffect } from "react";
import { business } from "../config/business";
import { useLanguage } from "../i18n/LanguageContext";
import { LANG_OPTIONS } from "../i18n/translations";

const OG_LOCALE = { en: "en_GB", es: "es_ES", pl: "pl_PL", it: "it_IT", fr: "fr_FR", pt: "pt_PT", uk: "uk_UA", ro: "ro_RO", zh: "zh_CN", hi: "hi_IN" };

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const langUrl = (code) => (code === "en" ? `${business.siteUrl}/` : `${business.siteUrl}/?lang=${code}`);

// page: { title, description, path, faq } para landing pages (solo inglés); sin page → home multilingüe
const Seo = ({ page }) => {
  const { lang, t } = useLanguage();

  useEffect(() => {
    const title = page?.title ?? t.meta.title;
    const description = page?.description ?? t.meta.description;
    const url = page ? `${business.siteUrl}/${page.path}` : langUrl(lang);
    const image = `${business.siteUrl}/images/og-image.jpg`;

    const pageLang = page ? "en" : lang;
    document.documentElement.lang = pageLang;
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: url });

    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:image"]', { property: "og:image", content: image });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: OG_LOCALE[pageLang] || "en_GB" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: business.name });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (!page) {
      [...LANG_OPTIONS.map((o) => o.code), "x-default"].forEach((code) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = code === "en" ? "en-GB" : code;
        link.href = langUrl(code === "x-default" ? "en" : code);
        document.head.appendChild(link);
      });
    }

    const areaServed = [
      { "@type": "City", name: business.mainArea, containedInPlace: { "@type": "Country", name: "United Kingdom" } },
      ...["Leith", "Portobello", "Morningside"].map((name) => ({ "@type": "Place", name, containedInPlace: { "@type": "City", name: business.mainArea } })),
      ...business.coverage.map((name) => ({ "@type": "City", name, containedInPlace: { "@type": "Country", name: "United Kingdom" } })),
    ];
    const services = t.services.categories.flatMap((c) => c.items.map((name) => ({ name, category: c.title })));

    setJsonLd("ld-business", {
      "@context": "https://schema.org",
      "@type": "Locksmith",
      "@id": `${business.siteUrl}/#business`,
      name: business.name,
      url: business.siteUrl,
      image,
      logo: `${business.siteUrl}/images/logo.webp`,
      description,
      telephone: business.phoneDisplay,
      email: business.email,
      inLanguage: pageLang,
      knowsLanguage: LANG_OPTIONS.map((o) => o.code),
      areaServed,
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: business.phoneDisplay,
        email: business.email,
        url: `https://wa.me/${business.whatsappNumber}`,
        availableLanguage: LANG_OPTIONS.map((o) => o.label),
      },
      sameAs: [business.social.facebook, business.social.instagram],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: t.services.title,
        itemListElement: services.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.name, category: s.category, areaServed, provider: { "@id": `${business.siteUrl}/#business` } },
        })),
      },
    });

    const faqItems = page?.faq ?? t.faq.items;
    setJsonLd("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: pageLang,
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }, [lang, t, page]);

  return null;
};

export default Seo;
